import axios from "axios";
import { EN } from "../i18n/en";
import { ES } from "../i18n/es";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach optional access token from localStorage (fallback for cross-site cookie issues)
api.interceptors.request.use((config) => {
  const tok = localStorage.getItem("cm_token");
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// --- Silent token refresh on 401 -----------------------------------------
// Quando o access token expira, o backend responde 401. Aqui chamamos
// /auth/refresh (usa o cookie refresh de 7 dias) UMA vez, guardamos o novo
// token e repetimos a requisição original — sem loops e com refresh único
// compartilhado entre requisições concorrentes. Isso mantém a sessão viva
// (ex.: super admin não é mais deslogado após ficar parado / no dia seguinte).
let _refreshPromise = null;
function _doRefresh() {
  if (!_refreshPromise) {
    const stored = localStorage.getItem("cm_refresh");
    _refreshPromise = api
      .post("/auth/refresh", stored ? { refresh_token: stored } : {})
      .then((r) => {
        const t = r.data?.access_token;
        const rt = r.data?.refresh_token;
        if (t) localStorage.setItem("cm_token", t);
        if (rt) localStorage.setItem("cm_refresh", rt);
        return t;
      })
      .finally(() => {
        _refreshPromise = null;
      });
  }
  return _refreshPromise;
}

const _NO_REFRESH = ["/auth/refresh", "/auth/login", "/auth/logout", "/auth/public/demo-login"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config) return Promise.reject(error);
    const url = config.url || "";
    if (config._retry || _NO_REFRESH.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    config._retry = true;
    try {
      const token = await _doRefresh();
      if (!token) throw new Error("refresh_failed");
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return api(config);
    } catch (e) {
      localStorage.removeItem("cm_token");
      localStorage.removeItem("cm_refresh");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("cm:auth-expired"));
      }
      return Promise.reject(error);
    }
  },
);

// Traduz mensagens (PT são as chaves) conforme o idioma salvo em localStorage.
// Mensagens não mapeadas caem no fallback (texto original em PT).
function _tr(s) {
  if (typeof s !== "string") return s;
  let lang = "pt";
  try {
    lang = localStorage.getItem("cm_lang") || "pt";
  } catch {
    lang = "pt";
  }
  const dict = lang === "en" ? EN : lang === "es" ? ES : null;
  return dict ? dict[s] ?? s : s;
}

export function formatApiError(detail) {
  if (detail == null) return _tr("Algo deu errado. Tente novamente.");
  if (typeof detail === "string") return _tr(detail);
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? _tr(e.msg) : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return _tr(detail.msg);
  return String(detail);
}

export function fileUrl(path) {
  const tok = localStorage.getItem("cm_token");
  const q = tok ? `?auth=${encodeURIComponent(tok)}` : "";
  return `${API_BASE}/files/${path}${q}`;
}
